// useTts: fetches synthesized audio from POST /api/tts and exposes a
// play/stop API plus loading state. Caches the audio blob per message
// so re-pressing play doesn't re-synthesize (saves cost + latency).
//
// The TTS endpoint is authed and returns audio/mpeg, not JSON, so we
// bypass useAuthedFetch's default JSON handling and read the body as
// a Blob.

import { useCallback, useEffect, useRef, useState } from 'react';
import { env } from '../env';
import { useSession } from './SessionProvider';

type PlaybackState = 'idle' | 'loading' | 'playing' | 'error';

export interface TtsHandle {
  state: PlaybackState;
  error: string | null;
  play: () => Promise<void>;
  stop: () => void;
  /** True when the browser supports HTMLAudioElement. */
  supported: boolean;
}

export function useTts(_messageId: string, text: string): TtsHandle {
  const { sessionToken } = useSession();
  const [state, setState] = useState<PlaybackState>('idle');
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  // Clean up object URL + audio element on unmount.
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  const stop = useCallback(() => {
    audioRef.current?.pause();
    if (audioRef.current) audioRef.current.currentTime = 0;
    setState('idle');
  }, []);

  const play = useCallback(async () => {
    if (state === 'playing') {
      stop();
      return;
    }
    if (!sessionToken) {
      setError('Not signed in');
      setState('error');
      return;
    }
    setError(null);
    // If we already fetched the audio for this messageId, just replay it.
    if (blobUrlRef.current && audioRef.current) {
      audioRef.current.currentTime = 0;
      setState('playing');
      try {
        await audioRef.current.play();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
      return;
    }
    setState('loading');
    try {
      const res = await fetch(`${env.apiBaseUrl}/api/tts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status} ${body.slice(0, 200)}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      const audio = new Audio(url);
      audio.onended = () => setState('idle');
      audio.onerror = () => {
        setError('Audio playback failed');
        setState('error');
      };
      audioRef.current = audio;
      setState('playing');
      await audio.play();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setState('error');
    }
  }, [sessionToken, text, state, stop]);

  return {
    state,
    error,
    play,
    stop,
    supported: typeof window !== 'undefined' && typeof Audio !== 'undefined',
  };
}

// Static suppression key so a fresh useTts hook never picks up stale state.
// Exported so the messageId can be referenced from the consuming component.
export const TTS_MESSAGE_ID_PREFIX = 'siggy-';
